import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { listenToShortstayData } from "../../../../redux/shortstaysSlice";
import { listenToShortstayReservaionData } from "../../../../redux/shortstayReservationSlice";
import { fetchAllUsers } from "../../../../redux/authSlice";
import axios from "axios";

const GuestConfirmCancelShortstay: React.FC = () => {
  const { guestId: guestIdParam, listingId, reservationId } = useParams<{
    guestId: string;
    listingId: string;
    reservationId: string;
  }>();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { user } = useSelector((state: RootState) => state.auth);
  const listing = useSelector(
    (state: RootState) => state.shortstays.currentListing
  );
  const reservation = useSelector(
    (state: RootState) => state.shortstayReservations.reservationById
  );
  const allUsers = useSelector((state: RootState) => state.auth.allUsers);

  const [showModal, setShowModal] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [loading, setLoading] = useState(false);

  const guestId = guestIdParam || user?.userId || "";

  // Load listing, reservation, and users
  useEffect(() => {
    if (listingId) dispatch(listenToShortstayData(listingId));
    if (reservationId) dispatch(listenToShortstayReservaionData(reservationId));
    if (allUsers.length === 0) dispatch(fetchAllUsers());
  }, [dispatch, listingId, reservationId, allUsers.length]);

  const booking = reservation;
  const host = allUsers.find((u) => u.userId === listing?.hostId);

  // ⭐ Detect if already cancelled
  const isCancelled = reservation?.isCancelled === true;

  const handleCancel = () => setShowModal(true);

  const confirmCancel = async () => {
    if (isCancelled) return; // 🚫 safety stop
    if (!booking || !listing || !guestId) {
      alert("Missing booking, listing, or guest information.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `https://europe-west3-alreluxe.cloudfunctions.net/applyShortRefund`,
        {
          reservationId: booking.id,
          hostId: listing.hostId,
          guestId,
          listingId,
          dateKey: booking.checkIn,
          cancellationType: "guest",
          cancellationReason,
        }
      );

      navigate(`/shortbookings/${guestId}`);
    } catch (error: any) {
      console.error("❌ Cancel error:", error);
      alert(
        error?.response?.data?.message ||
          "Failed to cancel booking. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const getHostInitials = () => {
    if (!host) return "H";
    const f = host.firstName?.[0]?.toUpperCase() || "";
    const l = host.lastName?.[0]?.toUpperCase() || "";
    return `${f}${l}`;
  };




  const normalizeDate = (date: any) => {
  if (!date) return "-";

  // If Firestore Timestamp
  if (date.seconds) {
    return new Date(date.seconds * 1000);
  }

  return new Date(date);
};

const formatDate = (date: any) => {
  const d = normalizeDate(date);
  if (!(d instanceof Date) || isNaN(d.getTime())) return "-";

  // strip time to avoid timezone shifts
  const normalized = new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate()
  );

  return normalized.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

//=================================================================//
  return (
    <div style={styles.page}>
      <h2 style={styles.title}>Cancel Booking</h2>

      {/* Header Section */}
      <div style={styles.headerSplit}>
        <div style={styles.leftHalf}>
          {listing?.photos?.[0] ? (
            <img
              src={listing.photos[0]}
              alt="Listing"
              style={styles.previewPhoto}
            />
          ) : (
            <div style={styles.placeholder}>No Image</div>
          )}
        </div>

        <div style={styles.rightHalf}>
          {host ? (
            <>
              {host.profilePicture ? (
                <img
                  src={host.profilePicture}
                  alt="Host"
                  style={styles.avatarLarge}
                  onClick={() => navigate(`/aboutme/${host.userId}`)}
                />
              ) : (
                <div style={styles.initialsCircle}>{getHostInitials()}</div>
              )}

              <h3 style={styles.hostName}>
                {host.firstName} {host.lastName}
              </h3>
              <p style={styles.hostLabel}>Your Host</p>
            </>
          ) : (
            <p>No host found</p>
          )}
        </div>
      </div>

      {/* Booking Info */}
      <div style={styles.container}>
        <p>
          <strong>Listing:</strong> {listing?.title || "Unnamed Listing"}
        </p>
        <p>
<strong>Check-in:</strong> {formatDate(booking?.checkIn)}
        </p>
        <p>
<strong>Check-out:</strong> {formatDate(booking?.checkOut)}
        </p>
        <p>
          <strong>Duration Reserved:</strong> {booking?.reservedDuration || "-"}
        </p>

        {/* ================================
            CANCEL BUTTON or DISABLED STATE
            ================================ */}
        {isCancelled ? (
          <div style={{ textAlign: "center", marginTop: 20 }}>
            <button
              style={{
                ...styles.cancelButton,
                background: "#555",
                cursor: "not-allowed",
                opacity: 0.6,
              }}
              disabled
            >
              Booking Already Cancelled
            </button>

            <button
              onClick={() => navigate(`/shortbookings/${guestId}`)}
              style={{
                marginTop: 12,
                padding: "12px 20px",
                borderRadius: 30,
                fontWeight: 600,
                border: "none",
                fontSize: "1rem",
                background: "#bf9b30",
                color: "#000",
                cursor: "pointer",
              }}
            >
              Back to My Bookings
            </button>
          </div>
        ) : (
          <button onClick={handleCancel} style={styles.cancelButton}>
            Cancel Booking
          </button>
        )}
      </div>

      {/* Modal Confirmation — ONLY if NOT cancelled */}
      {showModal && !isCancelled && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div className="glow-red" style={styles.glowBox}>
              <h3>Confirm Cancellation</h3>

              <p style={styles.warningText}>
                ⚠️ If you cancel, you will <strong>lose your service fee</strong>{" "}
                and <strong>one day’s rate</strong> will be paid to the host as
                compensation.
              </p>
              <p style={styles.noticeText}>
                You'll receive the remaining refund after deductions.
              </p>

              <textarea
                style={styles.textarea}
                placeholder="Optional: tell us why you’re cancelling"
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
              />
            </div>

            <div style={styles.modalActions}>
              <button
                onClick={confirmCancel}
                style={styles.confirmButton}
                disabled={loading}
              >
                {loading ? "Processing…" : "Yes, Cancel Booking"}
              </button>

              <button
                onClick={() => setShowModal(false)}
                style={styles.cancelModalButton}
                disabled={loading}
              >
                No, Go Back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestConfirmCancelShortstay;

/* ---------- STYLES ---------- */
const styles: { [key: string]: React.CSSProperties } = {
  page: {
    backgroundColor: "#000",
    minHeight: "100vh",
    padding: "80px 20px",
    color: "#fff",
    fontFamily: "Poppins, sans-serif",
  },
  title: {
    textAlign: "center",
    fontSize: "1.8rem",
    marginBottom: 20,
  },
  headerSplit: {
    display: "flex",
    width: "100%",
    maxWidth: 540,
    margin: "0 auto 20px",
    borderRadius: 16,
    overflow: "hidden",
    border: "1px solid #bf9b30",
  },
  leftHalf: {
    flex: 1,
    height: 200,
    background: "#111",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  rightHalf: {
    flex: 1,
    background: "#111",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: 10,
  },
  previewPhoto: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  placeholder: {
    width: "100%",
    height: "100%",
    color: "#999",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#222",
    fontSize: "0.9rem",
  },
  avatarLarge: {
    width: 70,
    height: 70,
    borderRadius: "50%",
    border: "2px solid #bf9b30",
    objectFit: "cover",
    cursor: "pointer",
  },
  initialsCircle: {
    width: 70,
    height: 70,
    borderRadius: "50%",
    background: "linear-gradient(145deg, #bf9b30, #8b6f25)",
    color: "#000",
    fontWeight: 700,
    fontSize: "1.1rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "2px solid #bf9b30",
  },
  hostName: {
    color: "#bf9b30",
    fontWeight: 600,
    fontSize: "1rem",
  },
  hostLabel: {
    color: "#aaa",
    fontSize: "0.85rem",
  },
  container: {
    background: "#111",
    borderRadius: 16,
    padding: 24,
    maxWidth: 540,
    margin: "0 auto",
    border: "1px solid #bf9b30",
  },
  cancelButton: {
    marginTop: 20,
    padding: "12px 20px",
    borderRadius: 30,
    fontWeight: 600,
    border: "none",
    fontSize: "1rem",
    background: "#ff4444",
    color: "#fff",
    cursor: "pointer",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  modal: {
    background: "#222",
    padding: 30,
    borderRadius: 16,
    textAlign: "center",
    color: "#fff",
    maxWidth: 400,
    width: "90%",
  },
  modalActions: {
    marginTop: 20,
    display: "flex",
    justifyContent: "space-around",
  },
  confirmButton: {
    padding: "10px 20px",
    borderRadius: 20,
    border: "none",
    background: "#bf9b30",
    color: "#000",
    cursor: "pointer",
    fontWeight: 600,
  },
  cancelModalButton: {
    padding: "10px 20px",
    borderRadius: 20,
    border: "none",
    background: "#555",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 600,
  },
  textarea: {
    width: "100%",
    marginTop: 10,
    padding: 10,
    borderRadius: 8,
    minHeight: 80,
    border: "1px solid #ccc",
    resize: "vertical",
  },
  warningText: {
    background: "rgba(255, 68, 68, 0.1)",
    border: "1px solid rgba(255, 68, 68, 0.4)",
    borderRadius: 12,
    padding: "10px 14px",
    color: "#ff6666",
    fontSize: "0.9rem",
    lineHeight: 1.5,
    textAlign: "left",
    marginBottom: 12,
  },
  noticeText: {
    color: "#bbb",
    fontSize: "0.9rem",
    marginBottom: 10,
  },
  glowBox: {
    padding: 20,
    borderRadius: 16,
    marginBottom: 16,
    background: "rgba(40, 0, 0, 0.5)",
    textAlign: "center",
  },
};
